import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { formatDecimalHours } from "../../utils/timeUtils";

// Suppress ResizeObserver errors
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = this;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

export default function MySalaryDetails() {
  const navigate = useNavigate();
  const { month: urlMonth, year: urlYear } = useParams();
  const [loading, setLoading] = useState(true);
  const [salaryInfo, setSalaryInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    presentDays: 0,
    absentDays: 0,
    totalDays: 0,
    currentMonthSalary: 0,
    totalWorkHours: 0,
    totalBreakHours: 0,
  });

  // Use URL params if available, otherwise use current month
  const currentDate = new Date();
  const month = urlMonth || (currentDate.getMonth() + 1).toString();
  const year = urlYear || currentDate.getFullYear().toString();

  useEffect(() => {
    fetchSalaryData();
    fetchPaymentTransactions();
  }, [month, year]);

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch salary information with staff type
      const salaryResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-salary-with-type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (salaryResponse.ok) {
        const salaryData = await salaryResponse.json();
        setSalaryInfo(salaryData);
      }

      // Fetch attendance records for selected month stats
      const recordsResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-attendance/records-with-tracking?month=${month}&year=${year}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (recordsResponse.ok) {
        const recordsData = await recordsResponse.json();
        setStats(recordsData.stats || stats);
      }
    } catch (error) {
      console.error("Error fetching salary data:", error);
      toast.error("Failed to load salary data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentTransactions = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch my payment transactions directly
      const paymentsResponse = await fetch(`${API_BASE_URL}/api/staff/my-salary-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (paymentsResponse.ok) {
        const data = await paymentsResponse.json();
        
        // Filter for this specific month
        const monthRecord = data.transactions?.find(r => {
          const recordMonth = r.SalaryMonth;
          return recordMonth === `${year}-${String(month).padStart(2, '0')}` || 
                 (recordMonth.includes(month) && recordMonth.includes(year));
        });
        
        if (monthRecord) {
          setTransactions(monthRecord.transactions || []);
        } else {
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error("Error fetching payment transactions:", error);
    }
  };

  const getSelectedMonth = () => {
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  const totalPaid = transactions.reduce((sum, t) => sum + parseFloat(t.AmountPaid || 0), 0);

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            bgcolor: "#1a1a1a",
          }}
        >
          <CircularProgress sx={{ color: "#C59A58" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 }, minHeight: "calc(100vh - 80px)", overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(salaryInfo?.staffType === "Task-Based" ? "/my-salary" : "/my-attendance-salary")}
            sx={{ color: "#C4A574", mr: 2 }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" }
            }}
          >
            Salary Details - {getSelectedMonth()}
          </Typography>
        </Box>

        {/* Salary Information */}
        <Card sx={{ mb: 3, boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Salary Information - {getSelectedMonth()}
            </Typography>
            <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.04)" }} />
            <Grid container spacing={3} sx={{ justifyContent: 'center'}}>
                {salaryInfo?.staffType === "Task-Based" ? (
                  <>
                    {/* Base Salary */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            ₹{parseFloat(salaryInfo?.monthlySalary || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Base Salary
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Salary Generated (renamed from Task Earnings) */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            ₹{(stats.taskEarnings || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Salary Generated
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Total Tasks Assigned */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.totalTasksAssigned || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Tasks Assigned
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Tasks Completed */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.tasksCompleted || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Tasks Completed
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Salary Received */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            ₹{totalPaid.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Salary Received
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* Hourly Staff Cards */}
                    {/* Total Working Hours */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.totalDays * 9} hrs
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Total Working Hours
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Working Hours Completed */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {formatDecimalHours(stats.totalWorkHours || 0)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Hours Completed
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Base Salary */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            ₹{parseFloat(salaryInfo?.monthlySalary || 0).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Base Salary
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Salary Generated */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: "#FFB74D"
                          }}>
                            ₹{stats.currentMonthSalary?.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Salary Generated
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Working Days */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.totalDays}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Working Days
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Present Days */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.presentDays}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Present Days
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Absent Days */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            {stats.absentDays}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Absent Days
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Holidays (if available) */}
                    {stats.holidayCount !== undefined && (
                      <Grid item xs={6} sm={4} md={3}>
                        <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                          <CardContent sx={{ textAlign: "center", py: 2 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                              {stats.holidayCount}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                              Holidays
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {/* Salary Received */}
                    <Grid item xs={6} sm={4} md={3}>
                      <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                            ₹{totalPaid.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                            Salary Received
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

        {/* Final Salary Card */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d", mb: 3 }}>
          <CardContent>
            <Box sx={{ p: 2, bgcolor: "#4a4a4a", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                {salaryInfo?.staffType === "Task-Based" ? (
                  <>
                    <strong style={{ color: "#C4A574" }}>Note:</strong> Your salary includes a base amount plus additional payment for each completed task. 
                    Task earnings are added when you mark your assignments as complete.
                  </>
                ) : (
                  <>
                    <strong style={{ color: "#C4A574" }}>Note:</strong> Salary calculation accounts for holidays and absent days. Sundays are excluded from working days.
                    {stats.holidayCount !== undefined && (
                      <> This month has <strong style={{ color: "#C4A574" }}>{stats.holidayCount} holiday(s)</strong> which reduces the required working days from {stats.baseWorkingDays} to {stats.totalDays}.</>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem" } }}>
              Payment History
            </Typography>

            {transactions.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: "#4a4a4a" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#2b2b2b" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.TransactionId} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" } }}>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>
                          {new Date(transaction.PaymentDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#A5D6A7", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>
                          ₹{parseFloat(transaction.AmountPaid).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.PaymentMode}</TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.TransactionReference || "-"}</TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.Remarks || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ color: "#9CA3AF" }}>
                  No payments received yet for this month
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
